import moment from 'moment-timezone';
import { useEffect } from 'react';
import { Field } from 'redux-form';

import { getLeavetime, parseTime, parseTimeSlots } from '@utils/helpers';

import Error from '@common/components/Error';
import StyledError from '@common/components/FormElements/styled/Error';
import Portal from '@common/components/Portal';
import { StartDatePicker } from '@common/components/StartDatePicker';

import { Router } from 'shared-routing';

import { Button } from '@common/components/Button';
import { Checkbox } from '@common/components/Checkbox/Checkbox';
import { ReduxFormSelect } from '@common/components/FormElements/BlockSelectField';

import { DatesSlider } from '@common/components/DatesSlider';

import { AlertWithChildren } from '@common/components/Alert/Alert';
import { ReadOnlyTextField, TextareaField } from '@common/components/FormElements';
import { ReduxFormGuestPicker } from '@common/components/GuestPicker';
import { useGetTableDepositValue } from '@common/features/tables/hooks/useGetTableDepositValue';
import { useGetTablesPage } from '@common/features/tables/hooks/useGetTablesPage';
import { usePrevious } from '@common/hooks/usePrevious';
import { some } from '@utils/some';
import {
  AdditionalInfoWrapper,
  AdditionNotesWrapper,
  DepositAmount,
  DepositDescription,
  FieldSpacer,
  FlexContainer,
  FormBox,
  ReturnMessage,
  Section,
} from './TableForm.styles';
import { TableFormReduxProps } from './TableForm.types';

const getSelectedTimeSlot = (timeSlots, time) => {
  if (!timeSlots || !time) {
    return null;
  }
  const selectedDate = timeSlots?.find(
    (timeslot) => timeslot.date === moment(time).format('YYYY-MM-DD')
  );
  const selectedTimeslot = selectedDate?.data.timeSlots.find((slot) => slot.timeSlot === time);
  return selectedTimeslot;
};

const isPromotionRequired = (timeslot) => timeslot && !timeslot.hasStandardAvailability;

export const TableForm = (props: TableFormReduxProps): JSX.Element => {
  const {
    changeJourneyParam,
    checkAvailability,
    dates,
    fetchRestaurantConfig,
    formFieldChange,
    guests,
    handleSubmit,
    id,
    maxPartyFallback,
    onGuestsInputChanged,
    onGuestsInputClose,
    onGuestsInputOpen,
    promotion,
    promotions,
    restaurant,
    restaurantConfig,
    restaurants = [],
    startDate,
    t,
    tablesDisableChildBookings,
    timeslot,
    timeslotError,
    timeSlots,
    visitDate,
    property,
    hasDietaryRequirements,
    hasCelebrationOptions,
  } = props;

  const prevProps = usePrevious(props);
  const { data: tablesPage } = useGetTablesPage(property.id, property.defaultLanguage);
  const { dietaryRequirements = [], celebrationOptions = [] } = tablesPage || {
    dietaryRequirements: [],
    celebrationOptions: [],
  };
  const selectedRestaurant = restaurants.find((rest) => {
    return rest.config.name === restaurant?.value || rest.restaurantName === restaurant?.value;
  });

  const isSubmitDisabled = () => {
    const selectedTimeslot = getSelectedTimeSlot(timeSlots, timeslot);

    if (!selectedRestaurant) return true;

    if (!timeslot || !selectedTimeslot) return true;

    if (isPromotionRequired(selectedTimeslot) && !promotion?.id) return true;

    return false;
  };

  useEffect(() => {
    if (props && prevProps) {
      const configFields = ['restaurant', 'visitDate'];

      const isChangedConfigField = checkFieldsChanges(prevProps, configFields);
      const isNotEmptyConfigField = checkFieldsExistance(configFields);

      if (isChangedConfigField && isNotEmptyConfigField) {
        formFieldChange('tableForm', 'timeslot', '');
        const values = configFields.reduce(
          (valuesObject, field) => ({
            ...valuesObject,
            [field]: props[field],
          }),
          {}
        );
        fetchRestaurantConfig(values);
      }
    }
  }, [props]);

  const getDepositPrice = (amount: number, currency = 'GBP', userLocale = 'EN-gb') => {
    return new Intl.NumberFormat(userLocale, { style: 'currency', currency })
      .formatToParts(amount / 100)
      .map((val) => val.value)
      .join('');
  };

  const { data: tableDepositValue, isLoading: tableDepositValueIsLoading } =
    useGetTableDepositValue(
      property.uuid,
      parseInt(guests?.adults || 1) + parseInt(guests?.children || 0),
      visitDate?.format('YYYY-MM-DD') || '',
      selectedRestaurant?.config?.id,
      promotion?.id
    );

  function getPromotions(promotions, timeSlot, restaurantConfig) {
    const getLabel = (promotion, filledPromotion) =>
      promotion.price > 0
        ? `${filledPromotion.name} - ${promotion.price} ${
            restaurantConfig ? restaurantConfig.currencySymbol : ''
          }`
        : filledPromotion.name;
    if (timeSlot && promotions.length) {
      return (
        timeSlot.availablePromotions?.reduce((acc, promotion) => {
          const filledPromotion = promotions.find(
            (promotionFull) => String(promotionFull.id) === String(promotion.id)
          );
          if (filledPromotion) {
            acc.push({
              value: {
                id: filledPromotion.id,
                timeslotReservationId: promotion.timeslotReservationId,
              },
              label: getLabel(promotion, filledPromotion),
            });
          }
          return acc;
        }, []) || []
      );
    }
    return [];
  }

  function getData({ inputDate = null, inputRestaurant = null, inputGuests = null }) {
    checkAvailability({
      guests: inputGuests || guests,
      restaurant: inputRestaurant || restaurant.restaurantName,
      visitDate: inputDate || startDate,
    });
  }

  function checkFieldsExistance(fields) {
    return fields.every((field) => !!props[field] || props[field] === 0);
  }

  function checkFieldsChanges(prevProps, fields) {
    return fields.some((field) => props[field] !== prevProps[field]);
  }

  const timeSlotOptions = parseTimeSlots(timeSlots);
  const selectedTimeslot = getSelectedTimeSlot(timeSlots, timeslot);
  const promotionOptions = getPromotions(promotions, selectedTimeslot, restaurantConfig);
  const maxBookingDate =
    restaurantConfig && restaurantConfig?.acceptBookingsDaysInAdvance > 0
      ? moment().add(restaurantConfig.acceptBookingsDaysInAdvance, 'days')
      : null;

  const leaveTime = getLeavetime(selectedTimeslot, promotion?.id);

  const handleChangeGuests = (_, inputGuests) => {
    const { adults, children } = inputGuests;
    changeJourneyParam('tableAdults', adults);
    changeJourneyParam('tableChildren', children);
    onGuestsInputChanged();
    onGuestsInputOpen();
  };

  useEffect(() => {
    if (selectedTimeslot) {
      formFieldChange(
        'tableForm',
        'timeslotReservationId',
        selectedTimeslot?.timeslotReservationId
      );
    }
  }, [selectedTimeslot]);

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Section>
        <FormBox>
          {restaurants.length > 1 ? (
            <Field
              component={ReduxFormSelect}
              labelWidth="150px"
              name="restaurant"
              options={restaurants}
              parse={(restaurant) => ({
                value: restaurant.value,
                restaurantName: restaurant.restaurantName,
              })}
              format={(args) => {
                return restaurants.find(
                  (restaurant) =>
                    restaurant.value === args?.value || restaurant?.restaurantName === args?.value
                );
              }}
              required
              label={t('tables.restaurantSelect')}
              handleAfterChange={({ restaurantName }) => {
                getData({ inputRestaurant: restaurantName });
                // Unsetting the restaurant that was clicked on the 'book now' page if the restaurant is changed in the dropdown
                Router.replace('/tables/book');
              }}
              useNativeDropdownOnMobile={false}
            />
          ) : (
            <Field
              component={ReadOnlyTextField}
              name="restaurant"
              label={t('tables.restaurantSelect')}
              displayText={restaurants?.[0]?.label}
              value={restaurants?.[0]?.value}
              required
            />
          )}
          <FieldSpacer>
            <Field
              // @todo 2024-01-14: This type override exists because of a typing mismatch on onChange, which needs straightened out.
              component={ReduxFormGuestPicker as any}
              name="guests"
              required
              onClose={onGuestsInputClose}
              onChange={handleChangeGuests}
              minTotal={restaurantConfig?.minOnlinePartySize ?? 1}
              maxTotal={restaurantConfig?.maxOnlinePartySize ?? maxPartyFallback}
              tablesDisableChildBookings={tablesDisableChildBookings}
            />
          </FieldSpacer>
          {!!timeslotError && (
            <StyledError labelWidth="150px">
              <Error error={timeslotError.partySize} />
            </StyledError>
          )}
        </FormBox>
        <FormBox>
          <Field
            component={StartDatePicker}
            name="startDate"
            popperModifiers={{
              preventOverflow: {
                enabled: true,
                escapeWithReference: false,
                boundariesElement: 'viewport',
              },
            }}
            maxDate={maxBookingDate}
            handleAfterChange={(data) => {
              formFieldChange('tableForm', 'visitDate', null);
              getData({ inputDate: data });
            }}
          />
          <Field
            component={DatesSlider}
            labelWidth="150px"
            name="visitDate"
            visitDate={visitDate}
            dates={dates}
            label={t('userAccount.marketingPreference')}
          />
          <Field
            component={ReduxFormSelect}
            name="timeslot"
            isRequired
            parse={(timeslot) => timeslot.value}
            format={(value) =>
              value ? timeSlotOptions.find((timeslot) => timeslot.value === value) : ''
            }
            placeholder={t('product.selectPlaceholder')}
            options={timeSlotOptions.filter(({ date }) => date === visitDate?.format('YYYY-MM-DD'))}
            handleAfterChange={() =>
              isPromotionRequired(selectedTimeslot)
                ? formFieldChange('tableForm', 'promotion', null)
                : null
            }
            noOptionsMessage={() => t('tables.noTimeslots')}
            label={t('tables.timeSelect')}
            useNativeDropdownOnMobile={false}
          />
          {promotionOptions && !!promotionOptions.length && (
            <FieldSpacer>
              <Field
                component={ReduxFormSelect}
                labelWidth="150px"
                name="promotion"
                options={promotionOptions}
                parse={(promotion) => promotion?.value || null}
                format={(value) =>
                  promotion?.id
                    ? promotionOptions.find((promotion) => promotion.value === value.id)
                    : null
                }
                isRequired={isPromotionRequired(selectedTimeslot)}
                isClearable={!isPromotionRequired(selectedTimeslot)}
                noOptionsMessage={() => t('tables.noMenus')}
                label={t('tables.menuSelect')}
                useNativeDropdownOnMobile={false}
              />
            </FieldSpacer>
          )}
          {selectedTimeslot && selectedTimeslot.isLeaveTimeRequired && (
            <ReturnMessage>{t('tables.returnInfo', { time: parseTime(leaveTime) })}</ReturnMessage>
          )}
          {tableDepositValue &&
            selectedTimeslot &&
            !tableDepositValueIsLoading &&
            ((!!promotionOptions.length && promotion?.id) || !promotionOptions.length) && (
              <>
                <AlertWithChildren testId="table-deposit-alert">
                  <FlexContainer>
                    <div>
                      <DepositAmount>
                        {getDepositPrice(
                          Number(tableDepositValue.cost.amount),
                          tableDepositValue?.cost?.currency || 'gbp'
                        )}{' '}
                      </DepositAmount>
                      <DepositDescription>
                        is required to secure this booking.{' '}
                        {tableDepositValue?.description && tableDepositValue.description}
                      </DepositDescription>
                    </div>
                  </FlexContainer>
                </AlertWithChildren>
              </>
            )}
          {some(dietaryRequirements) && (
            <AdditionalInfoWrapper>
              <Field
                component={({ input }) => (
                  <Checkbox
                    id="dietary-requirements-checkbox"
                    label={t('tables.dietaryRequirementsText')}
                    checked={input.value || false}
                    onChange={input.onChange}
                  />
                )}
                name="hasDietaryRequirements"
                type="checkbox"
              />
              {hasDietaryRequirements && (
                <>
                  <Field
                    component={ReduxFormSelect}
                    name="dietaryRequirements"
                    options={dietaryRequirements.map((option) => ({
                      value: option,
                      label: option,
                    }))}
                    isMulti
                    id="dietary-requirements-select"
                    noOptionsMessage={() => t('tables.noDietaryRequirementOptions')}
                    useNativeDropdownOnMobile={false}
                    parse={(selected) => (selected ? selected.map((option) => option.value) : [])}
                    format={(values) =>
                      values ? values.map((value) => ({ value, label: value })) : []
                    }
                  />
                </>
              )}
            </AdditionalInfoWrapper>
          )}
          {some(celebrationOptions) && (
            <AdditionalInfoWrapper>
              <Field
                component={({ input }) => (
                  <Checkbox
                    id="celebration-options-checkbox"
                    label={t('tables.specialOccasionText')}
                    checked={input.value || false}
                    onChange={input.onChange}
                  />
                )}
                name="hasCelebrationOptions"
                type="checkbox"
              />
              {hasCelebrationOptions && (
                <>
                  <Field
                    component={ReduxFormSelect}
                    name="celebrationOptions"
                    options={celebrationOptions.map((option) => ({
                      value: option,
                      label: option,
                    }))}
                    isMulti
                    noOptionsMessage={() => 'No celebration options available'}
                    id="celebration-options-select"
                    useNativeDropdownOnMobile={false}
                    parse={(selected) => (selected ? selected.map((option) => option.value) : [])}
                    format={(values) =>
                      values ? values.map((value) => ({ value, label: value })) : []
                    }
                  />
                </>
              )}
            </AdditionalInfoWrapper>
          )}
        </FormBox>
        <AdditionNotesWrapper>
          <Field
            isFullWidth
            component={TextareaField}
            name="note"
            label={t('tables.notes')}
            id="additional-notes"
          />
        </AdditionNotesWrapper>
      </Section>
      <Portal selector={`#${id}`}>
        <Button type="button" isPrimary onClick={handleSubmit} disabled={isSubmitDisabled()}>
          {t('tables.addToBasket')}
        </Button>
      </Portal>
    </form>
  );
};
